import React, { useLayoutEffect, useState } from 'react'
import { Box, Collapse } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useTheme } from '@mui/material/styles'
import { useIsMobileLayout } from '../../utils/hooks/useIsMobileLayout'
import { useSidebarContext } from '../../utils/providers/sidebarContext'
import { getVisibleTabNavItems, useTabNavContext } from '../../utils/providers/tabNavContext'
import LayoutNavSecondary from './LayoutNavSecondary'
import Sidebar from './sidebar/Sidebar'
import { ErrorBoundary } from '../error/ErrorBoundary'

export const SIDEBAR_SHELL_TRANSITION_MS = 300

const LayoutSidebarShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme()
  const isSmallScreen = useIsMobileLayout()
  const { companySlug } = useSidebarContext()
  const { secondaryItems } = useTabNavContext()
  const hasSidebar = companySlug !== null
  const hasSecondaryNav = hasSidebar || getVisibleTabNavItems(secondaryItems).length > 0
  const [renderedSlug, setRenderedSlug] = useState<string | null>(companySlug)
  const sidebarSlug = isSmallScreen ? companySlug : renderedSlug

  useLayoutEffect(() => {
    if (isSmallScreen) return
    if (companySlug) setRenderedSlug(companySlug)
  }, [companySlug, isSmallScreen])

  const handleSidebarExited = (): void => {
    if (!companySlug) setRenderedSlug(null)
  }

  const sidebar =
    sidebarSlug &&
    (isSmallScreen ? (
      <Sidebar companySlug={sidebarSlug} />
    ) : (
      <Box sx={{ width: theme.layout.sidebarWidth, height: '100%', overflow: 'hidden' }}>
        <Sidebar companySlug={sidebarSlug} />
      </Box>
    ))

  return (
    <Grid container wrap="nowrap" sx={{ width: '100%', height: '100%' }}>
      {isSmallScreen ? (
        sidebar
      ) : (
        <Collapse
          data-testid="layoutSidebarShellCollapse"
          in={hasSidebar}
          orientation="horizontal"
          timeout={SIDEBAR_SHELL_TRANSITION_MS}
          onExited={handleSidebarExited}
          sx={{ height: '100%', flexShrink: 0 }}
        >
          {sidebar}
        </Collapse>
      )}
      <Grid
        sx={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          height: '100%',
          minWidth: 0,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          transition: theme.transitions.create('flex', {
            duration: SIDEBAR_SHELL_TRANSITION_MS,
          }),
        }}
      >
        <Collapse
          data-testid="layoutSidebarShellSecondaryCollapse"
          in={hasSecondaryNav}
          timeout={SIDEBAR_SHELL_TRANSITION_MS}
        >
          <LayoutNavSecondary />
        </Collapse>
        <Grid
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <ErrorBoundary>
            <Box
              component="main"
              sx={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
              }}
            >
              {children}
            </Box>
          </ErrorBoundary>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default LayoutSidebarShell
